import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { HardDrive } from 'lucide-react'
import { fetchBackupHosts } from '@/api/backup'
import { CloudSyncConnectionsPanel } from '@/components/CloudSyncConnectionsPanel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { CollectedAtEmphasis, joinCollapsedSummary } from '@/components/SnapshotCard'
import { formatDateTime } from '@/lib/formatAgent'
import {
  altaroStatusBadgeVariant,
  altaroStatusLabel,
} from '@/lib/altaroBackup'
import { hostKindLabel } from '@/lib/hyperBackup'
import {
  normaliseToolkitStatus,
  toolkitStatusLabel,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

const FILTER_LABELS = {
  pass: 'Pass',
  warn: 'Warning',
  fail: 'Failed',
  no_data: 'No data',
  stale: 'Collection stale',
}

function isAltaroHost(hostKind) {
  return hostKind === 'altaro' || hostKind === 'mixed'
}

function computeBackupSummary(hosts) {
  const summary = {
    configured_hosts: hosts.length,
    reporting_hosts: 0,
    stale_hosts: 0,
    no_data_hosts: 0,
    pass_hosts: 0,
    warn_hosts: 0,
    fail_hosts: 0,
    total_vms: 0,
    missing_vms: 0,
    total_tasks: 0,
    total_cloudsync_connections: 0,
  }

  for (const host of hosts) {
    summary.total_vms += Number(host.vm_count || 0)
    summary.missing_vms += Array.isArray(host.missing_vms) ? host.missing_vms.length : 0
    summary.total_tasks += Number(host.task_count || 0)
    summary.total_cloudsync_connections += Number(host.cloudsync_count || 0)

    const ingest = host.ingest_status
    if (ingest === 'never') {
      summary.no_data_hosts += 1
    } else if (ingest === 'stale') {
      summary.stale_hosts += 1
      summary.reporting_hosts += 1
    } else {
      summary.reporting_hosts += 1
    }

    const status = normaliseToolkitStatus(host.overall_status)
    if (status === 'PASS') summary.pass_hosts += 1
    else if (status === 'WARN') summary.warn_hosts += 1
    else if (status === 'FAIL') summary.fail_hosts += 1
  }

  return summary
}

function SummaryTile({ label, value, variant, active, onClick }) {
  const clickable = Boolean(onClick) && ((value ?? 0) > 0 || active)
  const Tag = clickable ? 'button' : 'div'

  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      className={cn(
        'rounded-lg border border-border p-4 text-left transition-colors',
        clickable && 'cursor-pointer hover:border-primary/60 hover:bg-muted/40',
        active && 'border-primary bg-primary/5 ring-1 ring-primary/20',
        variant === 'success' && 'border-emerald-500/20 bg-emerald-500/5',
        variant === 'warning' && 'border-amber-500/30 bg-amber-500/5',
        variant === 'destructive' && 'border-destructive/40 bg-destructive/5',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value ?? 0}</p>
    </Tag>
  )
}

function IngestBadge({ status }) {
  if (status === 'active') return <Badge variant="success">Reporting</Badge>
  if (status === 'stale') return <Badge variant="warning">Collection stale</Badge>
  return <Badge variant="secondary">No data</Badge>
}

function VmSummaryTable({ vms }) {
  const rows = vms ?? []
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No guest VMs found in the latest snapshot.
      </p>
    )
  }

  return (
    <div className="rounded-md border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Guest VM</th>
            <th className="px-3 py-2 font-medium">Last backup</th>
            <th className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((vm, index) => {
            const isMissing = Boolean(vm.is_missing)
            const status = isMissing
              ? 'missing'
              : (vm.last_backup_status || vm.last_status)
            const statusLabel = isMissing
              ? 'Not in backup'
              : (vm.last_backup_status_label || vm.last_status_label)
            return (
              <tr
                key={`${vm.vm_name || 'unknown'}-${index}`}
                className={cn(
                  'border-b border-border last:border-0 align-top',
                  isMissing && 'bg-destructive/5',
                  vm.is_backup_host && 'bg-muted/30',
                )}
              >
                <td className="px-3 py-2 font-medium">
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {vm.vm_name || 'Unknown VM'}
                    {vm.is_backup_host ? (
                      <Badge variant="secondary">Backup host</Badge>
                    ) : null}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                  {isMissing ? '—' : formatDateTime(vm.last_backup_at || vm.last_event_at)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {status ? (
                    <Badge
                      variant={
                        vm.is_backup_host
                          ? 'secondary'
                          : altaroStatusBadgeVariant(status)
                      }
                    >
                      {vm.is_backup_host
                        ? altaroStatusLabel(status, statusLabel) + ' (ignored)'
                        : altaroStatusLabel(status, statusLabel)}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function TaskSummaryTable({ tasks }) {
  const rows = tasks ?? []
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No Hyper Backup tasks found in the latest snapshot.
      </p>
    )
  }

  return (
    <div className="rounded-md border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Backup task</th>
            <th className="px-3 py-2 font-medium">Last run</th>
            <th className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((task, index) => (
            <tr
              key={`${task.task_name || 'unknown'}-${index}`}
              className="border-b border-border last:border-0 align-top"
            >
              <td className="px-3 py-2 font-medium">{task.task_name || 'Unknown task'}</td>
              <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                {formatDateTime(task.last_run_at)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {task.status ? (
                  <Badge variant={altaroStatusBadgeVariant(task.status)}>
                    {altaroStatusLabel(task.status, task.status_label)}
                  </Badge>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}


function SourceCollectedAt({ label, collectedAt, ingestStatus }) {
  const hasData = ingestStatus !== 'never'

  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {hasData ? (
          <CollectedAtEmphasis collectedAt={collectedAt} prefix="" className="text-sm" />
        ) : (
          <p className="text-sm text-muted-foreground">No data</p>
        )}
        {hasData && <IngestBadge status={ingestStatus} />}
      </div>
    </div>
  )
}

function BackupServerCard({ host }) {
  const [open, setOpen] = useState(false)
  const overall = host.overall_status ? toolkitStatusLabel(host.overall_status) : null
  const statusVariant = (() => {
    const normalised = normaliseToolkitStatus(host.overall_status)
    if (normalised === 'PASS') return 'ready'
    if (normalised === 'WARN') return 'pending'
    if (normalised === 'FAIL') return 'destructive'
    return 'inactive'
  })()

  const showAltaro = host.host_kind === 'altaro' || host.host_kind === 'mixed'
  const showHyperBackup =
    host.host_kind === 'synology' ||
    host.host_kind === 'synology_both' ||
    host.host_kind === 'mixed'
  const showCloudSync =
    host.host_kind === 'synology_cloudsync' ||
    host.host_kind === 'synology_both' ||
    host.host_kind === 'mixed'
  const showSynology = showHyperBackup || showCloudSync
  const deviceLink = host.agent_id && showAltaro ? `/agents/${host.agent_id}?tab=backup` : null
  const title = host.display_name || host.hostname
  const collapsedSummary = joinCollapsedSummary([
    host.customer_name || (showSynology && !showAltaro ? 'Synology NAS' : null),
    hostKindLabel(host.host_kind),
    overall,
    host.missing_vms?.length
      ? `${host.missing_vms.length} missing guest${host.missing_vms.length === 1 ? '' : 's'}`
      : null,
    host.ingest_status === 'never' ? (
      <CollectedAtEmphasis neverLabel="No data collected" />
    ) : host.collected_at ? (
      <CollectedAtEmphasis collectedAt={host.collected_at} />
    ) : null,
    host.ingest_status === 'stale' ? 'Collection stale' : null,
  ])

  return (
    <CollapsePanel
      id={`backup-host-${host.hostname}`}
      title={title}
      collapsedSummary={collapsedSummary || undefined}
      open={open}
      onOpenChange={setOpen}
      pill={overall || undefined}
      pillVariant={overall ? statusVariant : 'inactive'}
      className="h-full"
      bodyClassName="space-y-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{hostKindLabel(host.host_kind)}</Badge>
        <IngestBadge status={host.ingest_status} />
        {deviceLink && (
          <Link to={deviceLink} className="text-sm text-primary hover:underline">
            Open device backup tab
          </Link>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {host.customer_name || (showSynology && !showAltaro ? 'Synology NAS' : 'Unlinked host')}
        {(host.display_name && host.display_name !== host.hostname) || showSynology ? (
          <span>{' · '}{host.hostname}</span>
        ) : null}
      </p>

      {host.interpretation && (
        <p className="text-sm text-muted-foreground">{host.interpretation}</p>
      )}

      <div className="flex flex-wrap gap-4 text-sm">
        {showAltaro && (
          <div>
            <p className="text-xs text-muted-foreground">Guest VMs</p>
            <p className="font-semibold tabular-nums">{host.vm_count ?? 0}</p>
          </div>
        )}
        {showHyperBackup && (
          <div>
            <p className="text-xs text-muted-foreground">Hyper Backup tasks</p>
            <p className="font-semibold tabular-nums">{host.task_count ?? 0}</p>
          </div>
        )}
        {showCloudSync && (
          <div>
            <p className="text-xs text-muted-foreground">Cloud Sync connections</p>
            <p className="font-semibold tabular-nums">{host.cloudsync_count ?? 0}</p>
          </div>
        )}
      </div>

      {(showAltaro || showHyperBackup || showCloudSync) && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Last collected
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {showAltaro && (
              <SourceCollectedAt
                label="Altaro"
                collectedAt={host.altaro_collected_at}
                ingestStatus={host.altaro_ingest_status}
              />
            )}
            {showHyperBackup && (
              <SourceCollectedAt
                label="Hyper Backup"
                collectedAt={host.hyperbackup_collected_at}
                ingestStatus={host.hyperbackup_ingest_status}
              />
            )}
            {showCloudSync && (
              <SourceCollectedAt
                label="Cloud Sync"
                collectedAt={host.cloudsync_collected_at}
                ingestStatus={host.cloudsync_ingest_status}
              />
            )}
          </div>
        </div>
      )}

      {showCloudSync && (
        <CloudSyncConnectionsPanel
          connections={host.cloudsync_connections}
          postureStatus={host.cloudsync_status}
          count={host.cloudsync_connections?.length ?? host.cloudsync_count ?? 0}
        />
      )}

      {showAltaro && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Altaro guest VMs ({host.vms?.length ?? host.vm_count ?? 0})
          </p>
          {host.missing_vms?.length > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {host.missing_vms.length} expected guest
              {host.missing_vms.length === 1 ? '' : 's'} not in the latest Altaro report:{' '}
              {host.missing_vms.join(', ')}.
            </div>
          )}
          <VmSummaryTable vms={host.vms} />
        </div>
      )}

      {showHyperBackup && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Hyper Backup tasks ({host.tasks?.length ?? host.task_count ?? 0})
          </p>
          <TaskSummaryTable tasks={host.tasks} />
        </div>
      )}

      {host.host_kind === 'unknown' && (
        <p className="text-sm text-muted-foreground">
          No backup snapshot received yet for this host.
        </p>
      )}

      {deviceLink && (
        <Button asChild variant="outline" size="sm">
          <Link to={deviceLink}>Open device backup tab</Link>
        </Button>
      )}
    </CollapsePanel>
  )
}

export function Backup() {
  const { isAdmin } = useAuth()
  const [hosts, setHosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  useEffect(() => {
    setLoading(true)
    setError('')
    fetchBackupHosts()
      .then((hostsData) => {
        setHosts(hostsData.hosts ?? [])
      })
      .catch(() => {
        setHosts([])
        setError('Could not load backup status.')
      })
      .finally(() => setLoading(false))
  }, [])

  const serverHosts = useMemo(
    () => hosts.filter((host) => isAltaroHost(host.host_kind)),
    [hosts],
  )

  const summary = useMemo(
    () => computeBackupSummary(serverHosts),
    [serverHosts],
  )

  const filteredHosts = serverHosts.filter((host) => {
    if (statusFilter === 'all') return true
    if (statusFilter === 'no_data') return host.ingest_status === 'never'
    if (statusFilter === 'stale') return host.ingest_status === 'stale'
    const normalised = normaliseToolkitStatus(host.overall_status)
    if (statusFilter === 'pass') return normalised === 'PASS'
    if (statusFilter === 'warn') return normalised === 'WARN'
    if (statusFilter === 'fail') return normalised === 'FAIL'
    return true
  })

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <HardDrive className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold">Server Backup</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Altaro VM Backup status for configured backup servers. Synology NAS hosts are on the
            NAS Backup page.
          </p>
        </div>
        {isAdmin && (
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/settings?section=server-backup">Configure backup servers</Link>
          </Button>
        )}
      </div>

      {error && (
        <Card>
          <CardContent className="pt-6 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <SummaryTile
          label="Configured"
          value={summary.configured_hosts}
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
        />
        <SummaryTile
          label="Pass"
          value={summary.pass_hosts}
          variant="success"
          active={statusFilter === 'pass'}
          onClick={() => setStatusFilter((current) => (current === 'pass' ? 'all' : 'pass'))}
        />
        <SummaryTile
          label="Warning"
          value={summary.warn_hosts}
          variant="warning"
          active={statusFilter === 'warn'}
          onClick={() => setStatusFilter((current) => (current === 'warn' ? 'all' : 'warn'))}
        />
        <SummaryTile
          label="Failed"
          value={summary.fail_hosts}
          variant="destructive"
          active={statusFilter === 'fail'}
          onClick={() => setStatusFilter((current) => (current === 'fail' ? 'all' : 'fail'))}
        />
        <SummaryTile
          label="No data"
          value={summary.no_data_hosts}
          active={statusFilter === 'no_data'}
          onClick={() => setStatusFilter((current) => (current === 'no_data' ? 'all' : 'no_data'))}
        />
        <SummaryTile
          label="Collection stale"
          value={summary.stale_hosts}
          variant="warning"
          active={statusFilter === 'stale'}
          onClick={() => setStatusFilter((current) => (current === 'stale' ? 'all' : 'stale'))}
        />
        <SummaryTile label="Guest VMs" value={summary.total_vms} />
        <SummaryTile
          label="Missing guests"
          value={summary.missing_vms}
          variant="destructive"
        />
      </div>

      {statusFilter !== 'all' && (
        <p className="text-sm text-muted-foreground">
          Filter: {FILTER_LABELS[statusFilter] || statusFilter}.{' '}
          <button
            type="button"
            className="underline hover:text-foreground"
            onClick={() => setStatusFilter('all')}
          >
            Clear filter
          </button>
        </p>
      )}

      {serverHosts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="space-y-3 pt-6 text-sm text-muted-foreground">
            <p>No Altaro backup servers configured yet.</p>
            {isAdmin ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/admin/settings?section=server-backup">Add backup server hostnames in Settings</Link>
              </Button>
            ) : (
              <p>Ask an administrator to add Altaro backup server hostnames in Settings.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredHosts.map((host) => (
            <BackupServerCard key={host.hostname} host={host} />
          ))}
        </div>
      )}

      {serverHosts.length > 0 && filteredHosts.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No backup servers match the current filter.
          </CardContent>
        </Card>
      )}
    </div>
  )
}
